/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MOBILE_TABLET_MIGRATION_PHASE3
 * Date:         2026-02-09
 * Modified By:  Claude
 *
 * Description:
 * Created mobile card-based view for inventory table.
 * Displays inventory items as touch-friendly cards on mobile devices.
 *
 * File Size Check:
 * - Current: NEW FILE
 * - Projected: ~130 lines
 * - Decision: Proceed (within component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/inventory/InventoryTable.tsx
 */

import React from "react";
import { Badge, Button } from "react-bootstrap";
import { InventoryItem } from "../../../services/adminInventory";

interface MobileInventoryListProps {
  rows: InventoryItem[];
  onView: (item: InventoryItem) => void;
  onEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
  formatPrice: (item: InventoryItem) => React.ReactNode;
  formatDate: (date?: string) => string;
}

export const MobileInventoryList: React.FC<MobileInventoryListProps> = ({
  rows,
  onView,
  onEdit,
  onDelete,
  formatPrice,
  formatDate,
}) => {
  if (rows.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No inventory items found.</p>
      </div>
    );
  }

  return (
    <div className="mobile-list-container">
      {rows.map((item) => (
        <div key={item._id} className="inventory-mobile-card">
          {/* Header */}
          <div className="inventory-mobile-card__header">
            <div className="inventory-mobile-card__info">
              <h3 className="inventory-mobile-card__manufacturer">
                {item.manufacturer}
              </h3>
              <p className="inventory-mobile-card__part">
                {item.partNumber || "-"}
              </p>
            </div>
            <Badge bg={item.quantity > 0 ? "success" : "warning"}>
              QTY: {item.quantity}
            </Badge>
          </div>

          {/* Badges */}
          <div className="inventory-mobile-card__badges">
            <Badge bg="secondary" className="text-wrap">
              {item.condition}
            </Badge>
            {item.country && (
              <Badge bg="outline-secondary" className="border">
                {item.country}
              </Badge>
            )}
          </div>

          {/* Details grid */}
          <div className="inventory-mobile-card__grid">
            {item.model && (
              <div className="inventory-mobile-card__field">
                <span className="inventory-mobile-card__field-label">Model</span>
                <span className="inventory-mobile-card__field-value">
                  {item.model}
                </span>
              </div>
            )}
            {item.title && (
              <div className="inventory-mobile-card__field">
                <span className="inventory-mobile-card__field-label">Title</span>
                <span className="inventory-mobile-card__field-value">
                  {item.title}
                </span>
              </div>
            )}
            <div className="inventory-mobile-card__field">
              <span className="inventory-mobile-card__field-label">Price</span>
              <span className="inventory-mobile-card__field-value">
                {formatPrice(item)}
              </span>
            </div>
            <div className="inventory-mobile-card__field">
              <span className="inventory-mobile-card__field-label">Updated</span>
              <span className="inventory-mobile-card__field-value">
                {formatDate(item.updatedAt)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="mobile-list-card__actions">
            <Button
              variant="outline-primary"
              size="sm"
              className="mobile-list-card__action touch-target"
              onClick={() => onView(item)}
            >
              View
            </Button>
            <Button
              variant="outline-warning"
              size="sm"
              className="mobile-list-card__action touch-target"
              onClick={() => onEdit(item)}
            >
              Edit
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              className="mobile-list-card__action touch-target"
              onClick={() => onDelete(item)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MobileInventoryList;
